import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';

import CART_ITEM from '../../constants/cartItems';
import { getAlbumCustomPrintCartItemPrice } from '../../redux/helpers';
import TrashIcon from '../../../../components/icons/TrashIcon';
import { getAlbumCustomPrintName } from '../../../../util/custom-prints';
import { getIdentityName } from '../../../../util/identity';
import IdentityAvatar from '../../../../containers/global/identity/IdentityAvatarContainer';
import CustomPrintIcon from '../../../../components/icons/CustomPrintIcon';
import { CUSTOM_PRINT_TYPE } from '../../../../constants/pro-custom-prints';
import { getAsset } from '../../../../util/assets';
import CollageIcon from '../../../../components/icons/CollageIcon';
import CustomPrintPlaqueConfiguratorModal from '../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/modal';
import Button from '../../../../components/global/Button';
import { validateCustomPrintPhotoSelections } from '../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/helpers';
import CheckoutCartItemPriceDisplay from './CheckoutCartItemPriceDisplay';
import { isAlbumCustomPrintConfigurableInPlace } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/helpers';
import AccountCardCartItemPreview from './AccountCardCartItemPreview';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import CheckoutItemQuantityInput from './CheckoutItemQuantityInput';

const propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CART_ITEM.ALBUM_CUSTOM_PRINT]).isRequired,
    albumCustomPrint: PropTypes.shape({
      id: PropTypes.string.isRequired,
      custom_print: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    identity: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  pricing: PropTypes.shape({
    price: PropTypes.object,
    discount_price: PropTypes.object,
  }),
  loading: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

const AlbumCustomPrintCartItem = ({
  item,
  pricing,
  loading,
  onRemove,
  onUpdate,
}) => {
  const [configurationModalOpen, setConfigurationModalOpen] = useState(false);
  const {
    albumCustomPrint,
    identity,
    quantity,
    isAllPhotos,
    forceEntitlement,
  } = item;
  const price = getAlbumCustomPrintCartItemPrice(item);
  const photosNo = albumCustomPrint.photos_no;
  const identityName = getIdentityName(identity);

  return (
    <div className="checkout-cart-item checkout-album-custom-print-cart-item">
      <div className="checkout-cart-item-content">
        {(() => {
          if (
            albumCustomPrint.custom_print.type === CUSTOM_PRINT_TYPE.COLLAGE
          ) {
            return (
              <div className="checkout-cart-item-figure">
                <img src={getAsset('/collage-illustration.svg')} alt="" />
                <span className="checkout-cart-item-photo-icon">
                  <CollageIcon size={20} />
                </span>
              </div>
            );
          }

          if (item.configuration?.accountCard) {
            return <AccountCardCartItemPreview item={item} />;
          }

          return (
            <div className="checkout-cart-item-figure checkout-cart-item-avatar">
              <IdentityAvatar identity={identity}>
                <span className="checkout-cart-item-photo-icon">
                  <CustomPrintIcon color="#0e84fe" />
                </span>
              </IdentityAvatar>
            </div>
          );
        })()}

        <div className="checkout-cart-item-info">
          <span className="checkout-cart-item-title">
            <span className="text-truncate">
              {getAlbumCustomPrintName(albumCustomPrint)}
            </span>
          </span>

          <CheckoutCartItemPriceDisplay
            clientPrice={price}
            pricing={pricing}
            loading={loading}
          />

          <div className="checkout-cart-item-info-actions mt-2">
            <CheckoutItemQuantityInput
              onChange={value =>
                onUpdate(item.id, {
                  quantity: value,
                })
              }
              value={quantity}
            />

            <button
              className="checkout-cart-item-btn-remove"
              onClick={() => onRemove(item.id)}
            >
              <TrashIcon color="#fe5851" size={18} />
            </button>
          </div>

          {!isAlbumCustomPrintConfigurableInPlace(albumCustomPrint) && (
            <div className="checkout-cart-item-description">
              {albumCustomPrint.custom_print?.type !==
                CUSTOM_PRINT_TYPE.WALDOGRAM &&
                `Choose your fav ${photosNo} photos of ${identityName} after
                checkout.`}
            </div>
          )}
        </div>
      </div>
      {isAlbumCustomPrintConfigurableInPlace(albumCustomPrint) && (
        <Button
          btnSize="sm"
          btnStyle="link"
          className="text-extra-bold text-nowrap"
          onClick={() => setConfigurationModalOpen(true)}
        >
          View &amp; Edit
        </Button>
      )}
      <AccountCardAlbumCustomPrintConfiguratorModal
        defaultAccountCard={item.configuration?.accountCard}
        forceOpen={configurationModalOpen && item.configuration?.accountCard}
        albumCustomPrint={albumCustomPrint}
        albumMembershipGroup={item.albumMembershipGroup}
        onClose={() => setConfigurationModalOpen(false)}
        onFinished={({ accountCard }) => {
          onUpdate(item.id, {
            configuration: { accountCard },
          });
          setConfigurationModalOpen(false);
        }}
        buttonText="Done"
        isAllPhotos={isAllPhotos}
        forceEntitlement={forceEntitlement}
      />
      <CustomPrintPlaqueConfiguratorModal
        forceOpen={configurationModalOpen && !item.configuration?.accountCard}
        albumCustomPrint={albumCustomPrint}
        identity={identity}
        initialConfiguration={item.configuration}
        renderSubmit={config => {
          return (
            <Fragment>
              {validateCustomPrintPhotoSelections(config) && (
                <Button
                  onClick={() => {
                    onUpdate(item.id, {
                      configuration: config,
                    });
                    setConfigurationModalOpen(false);
                  }}
                  btnBlock
                  className="text-black"
                >
                  Done
                </Button>
              )}
              <Button
                btnStyle="link"
                btnBlock
                onClick={() => setConfigurationModalOpen(false)}
                className="text-center mt-2"
              >
                Dismiss
              </Button>
            </Fragment>
          );
        }}
      />
    </div>
  );
};

AlbumCustomPrintCartItem.propTypes = propTypes;

export default AlbumCustomPrintCartItem;
